import React, { useEffect, useState } from "react";
import { Loader2, GraduationCap } from "lucide-react";
import { api } from "@/lib/api";

const norm = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const groupLabel = (g) => (g || "").replace("|", " · ");
const avgOf = (vals) => {
  const p = vals.filter((v) => v !== null && v !== undefined);
  if (!p.length) return null;
  const x = p.reduce((a, b) => a + b, 0) / p.length; const b = Math.floor(x);
  return Math.min(10, b + ((Math.round((x - b) * 100) / 100) >= 0.6 ? 1 : 0));
};

function GroupCard({ g, terms, labels, pass, q }) {
  const [lim, setLim] = useState(8);
  const rows = g.rows.filter((r) => !q || norm(r.student_name).includes(norm(q))).sort((a, b) => a.student_name.localeCompare(b.student_name));
  const color = (v) => (v === null || v === undefined ? "text-nardo-300" : v < pass ? "text-red-600" : "text-emerald-600");
  return (
    <div className="bg-white rounded-2xl border border-nardo-200 shadow-sm overflow-hidden" data-testid={`calif-group-${g.key}`}>
      <div className="px-4 py-3 flex items-center gap-3 border-b border-nardo-100 bg-navy/5">
        <div className="min-w-0"><div className="font-semibold text-navy text-sm">{g.subject}</div><div className="text-[11px] text-nardo-500">{groupLabel(g.grade)} · {g.rows.length} alumnos</div></div>
        <div className="ml-auto text-right"><div className="text-[10px] uppercase tracking-wider text-nardo-500">Promedio grupo</div><div className={`text-xl font-extrabold ${color(g.avg)}`}>{g.avg ?? "—"}</div></div>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-wider text-nardo-500"><th className="text-left px-4 py-2">Alumno</th>{terms.map((t) => <th key={t} className="px-2 py-2">{labels[t] || t}</th>)}<th className="px-2 py-2">Prom.</th></tr></thead>
        <tbody className="divide-y divide-nardo-50">
          {rows.slice(0, lim).map((r) => (
            <tr key={r.student_id}><td className="px-4 py-1.5 text-navy">{r.student_name}</td>
              {terms.map((t) => <td key={t} className={`px-2 py-1.5 text-center font-semibold ${color(r[t])}`}>{r[t] ?? "—"}</td>)}
              <td className={`px-2 py-1.5 text-center font-extrabold ${color(r.avg)}`}>{r.avg ?? "—"}</td></tr>
          ))}
        </tbody>
      </table>
      {rows.length > lim && <button onClick={() => setLim((n) => n + 15)} data-testid={`calif-more-${g.key}`} className="w-full text-xs font-semibold text-navy py-2 border-t border-nardo-100 hover:bg-navy/5">Ver más ({rows.length - lim})</button>}
    </div>
  );
}

export default function CalificacionesDocentes() {
  const [data, setData] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [tq, setTq] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/direccion/calificaciones").then(({ data }) => setData(data)).catch(() => setData({ rows: [] })); }, []);
  if (!data) return <div className="py-16 text-center text-sm text-nardo-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando calificaciones…</div>;
  const terms = data.terms || ["t1", "t2", "t3"]; const labels = data.term_labels || {}; const pass = data.pass_grade || 7;
  const byT = {};
  data.rows.forEach((r) => {
    const t = (byT[r.docente_user_id] = byT[r.docente_user_id] || { key: r.docente_user_id, name: r.docente_name, groups: {} });
    const gk = `${r.subject}__${r.grade}`;
    (t.groups[gk] = t.groups[gk] || { key: gk, subject: r.subject, grade: r.grade, rows: [] }).rows.push(r);
  });
  const teachers = Object.values(byT).map((t) => {
    const groups = Object.values(t.groups).map((g) => ({ ...g, avg: avgOf(g.rows.map((r) => r.avg)) }));
    return { ...t, groups, avg: avgOf(groups.map((g) => g.avg)), students: groups.reduce((a, g) => a + g.rows.length, 0) };
  }).sort((a, b) => a.name.localeCompare(b.name));
  const sel = teachers.find((t) => t.key === teacher);
  const shownT = teachers.filter((t) => !tq || norm(t.name).includes(norm(tq)));
  return (
    <div className="space-y-4" data-testid="calif-docentes">
      <div className="bg-white rounded-2xl border border-nardo-200 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <GraduationCap className="w-5 h-5 text-navy" /><h3 className="font-display font-bold text-lg text-navy">Calificaciones por docente</h3>
          <input value={tq} onChange={(e) => setTq(e.target.value)} placeholder="Buscar docente…" data-testid="calif-teacher-search" className="ml-auto w-48 border border-nardo-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-navy" />
        </div>
        {teachers.length === 0 ? <p className="text-sm text-nardo-500 mt-3">Aún no hay calificaciones capturadas.</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 mt-4">
            {shownT.map((t) => {
              const on = t.key === teacher;
              return (
                <button key={t.key} onClick={() => setTeacher(on ? null : t.key)} data-testid={`calif-teacher-${t.key}`}
                  className={`text-left rounded-2xl border p-3 transition-all hover:-translate-y-0.5 hover:shadow-lg ${on ? "bg-gradient-to-br from-navy-light to-navy text-white border-transparent" : "bg-white border-nardo-200 text-navy"}`}>
                  <div className="text-[13px] font-semibold leading-tight line-clamp-2">{t.name}</div>
                  <div className={`text-[11px] mt-1 ${on ? "text-white/80" : "text-nardo-500"}`}>{t.groups.length} grupo(s) · {t.students} alumnos</div>
                  <div className="text-lg font-extrabold mt-1">Prom. {t.avg ?? "—"}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {sel && (
        <div className="space-y-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Buscar alumno de ${sel.name}…`} data-testid="calif-student-search" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy bg-white" />
          <div className="grid lg:grid-cols-2 gap-3">{sel.groups.map((g) => <GroupCard key={g.key} g={g} terms={terms} labels={labels} pass={pass} q={q} />)}</div>
        </div>
      )}
    </div>
  );
}
